import type { Module } from 'vuex'
import deepmerge from 'deepmerge'
import type {
  AREvent,
  ConnectZoomProviderInfo,
  EventAnnotation,
  EventState,
  EventTicketSalesChartData,
  EventTicketSalesChartRawData,
  EventTimescaleDataContexts,
  EventListType,
  EventsFilterDateRangeType,
  EventAudienceStatistics,
} from './types'
import { defaultSalesTimescaleContexts, defaultSalesChartContexts,
  initScratchEvent,
  getEventbriteUrl,
  patchMessageListInAdvancedSettings,
  patchTagsInAdvancedSettings,
  initScratchAdvancedSettings,
  patchScratchAdvancedSettingsToEvent,
  getZoomUrlFromCurrentEvent,
  generateDefaultEventStartTime,
  checkHasIntegration,
  getEventImageUrl,
  getEventTotalTicketSales,
  getEventTotalTicketSold,
  getEventAttendees } from './utils'
import { eventActions } from './actions'
import type { RootState } from '@/store/modules/types'
import type { MessageList } from '@/api/message-lists/types'
import { clone, mergeObjects } from '@/utils/helpers'

export const initialEventState = (): EventState => ({
  // Events
  events: [],
  totalEventsCount: 0,
  hasNoMoreEvents: false,
  isFetchingEvents: false,

  // Current Event
  currentEvent: null,
  isFetchingEvent: false,

  // Scratch Event
  scratchEvent: initScratchEvent(),
  isUpdatingEvent: false,
  isCreatingEvent: false,
  connectZoomProviderInfo: null,

  // Events Merge
  isMergingEvents: false,

  // Upcoming Events (combined events and groups)
  upcomingItems: [],
  totalUpcomingItemsCount: 0,
  hasNoMoreUpcomingItems: false,
  isFetchingUpcomingItems: false,

  // Past Events (combined events and groups)
  pastItems: [],
  totalPastItemsCount: 0,
  hasNoMorePastItems: false,
  isFetchingPastItems: false,

  // Archived Events (combined events and groups with archivedAt timestamp)
  archivedItems: [],
  totalArchivedItemsCount: 0,
  hasNoMoreArchivedItems: false,
  isFetchingArchivedItems: false,

  // Event Groups
  eventGroups: [],
  totalEventGroupsCount: 0,
  hasNoMoreEventGroups: false,
  isFetchingEventGroups: false,
  isCreatingEventGroup: false,
  isUpdatingEventGroup: false,
  isAddingEventsToGroup: false,
  isRemovingEventsFromGroup: false,
  currentEventGroup: null,

  // Event Ticket Stats & Sales
  isFetchingEventTicketStats: false,
  eventTicketStats: null,
  isFetchingEventTicketSales: false,
  eventTicketSales: null,
  isFetchingZoomAttendance: false,
  salesChartTimezone: null,

  // Event Orders
  eventOrders: [],
  isFetchingEventOrders: false,
  totalEventOrdersCount: 0,
  rawSalesChartData: null,
  rawSalesOverlayData: null,
  messageListInEvent: null,
  scratchAdvancedSettings: initScratchAdvancedSettings(),
  salesChartDataIsLoading: false,
  dragListenerAdded: false,
  eventAnnotations: [],
  eventLineAnnotations: [],
  activeLineAnnotation: null,
  isTooltipShowing: false,
  isLineAnnotationEditing: false,

  compareSalesData: defaultSalesChartContexts(),
  initialSalesData: defaultSalesChartContexts(),
  compareTimescaleData: defaultSalesTimescaleContexts(),
  initialTimescaleData: defaultSalesTimescaleContexts(),

  // eventList (used in CompareEventsDropdown)
  eventsList: [],
  isFetchingEventsList: false,
  eventsListSearch: '',
  eventsListTotalCount: 0,
  eventsListLoaded: 0,
  isLastEventListLoaded: false,
  eventsListStepLoadCount: 25,
  selectedEventsList: [],
  unsavedEventsList: [],

  // Events Page Filters
  eventsFilterDateRange: { selected: 'all', condition: null },
  eventsFilterOrderByItems: 'datecreated',
  eventsFilterGroupButton: 'tiles',
  eventsFilterProvider: 'all',
  eventsTypeFilter: 'all',

  eventAudienceStats: [],
  isFetchingEventAudienceStats: false,

  // Power Ups
  existingPowerUps: [],
  isCreatingPowerUp: false,
  isFetchingPowerUps: false,

  // Insights
  engagementInsights: null,
  isFetchingEngagementInsights: false,
})

export const initAdvancedSettings = {}

const eventModule: Module<EventState, RootState> = {
  namespaced: true,
  actions: eventActions,
  state: initialEventState,
  mutations: {
    SET_DRAG_LISTENER_STATE(state, payload) {
      state.dragListenerAdded = payload
    },
    UPDATE_ANNOTATION(state, annotation) {
      const updatedAnno = state.eventAnnotations.find(item => item.oid === annotation.oid)
      updatedAnno!.body = annotation.body
    },
    DELETE_ANNOTATION(state, oid) {
      state.eventAnnotations = state.eventAnnotations.filter(annotation => annotation.oid !== oid)
    },
    CLEAR_ANNOTATIONS(state) {
      state.eventAnnotations = []
    },
    SET_INITIAL_ANNOTATIONS(state, annotations) {
      state.eventAnnotations = annotations
    },
    ADD_ANNOTATION_TO_EVENT(state, annotation) {
      state.eventAnnotations = [
        ...state.eventAnnotations,
        annotation,
      ]
    },

    SET_LINE_ANNOTATIONS(state, annotations) {
      state.eventLineAnnotations = annotations
    },
    ADD_LINE_ANNOTATION(state, annotation) {
      state.eventLineAnnotations.push(annotation)
    },
    DELETE_LINE_ANNOTATION(state, oid) {
      state.eventLineAnnotations = state.eventLineAnnotations
        .filter(annotation => !(annotation.oid === oid && annotation.provider === 'custom'))
    },
    CHANGE_CUSTOM_LINE_ANNOTATION(state, { oid, body, title }) {
      state.eventLineAnnotations = state.eventLineAnnotations.map((annotation) => {
        if (annotation.oid === oid && annotation.provider === 'custom') {
          annotation.body = body
          annotation.title = title
        }
        return annotation
      })
    },
    SET_ACTIVE_LINE_ANNOTATION(state, annotation) {
      state.activeLineAnnotation = annotation
    },
    SET_IS_TOOLTIP_SHOWING(state, isShowing) {
      state.isTooltipShowing = isShowing
    },
    SET_IS_LINE_ANNOTATION_EDITING(state, isEditing) {
      state.isLineAnnotationEditing = isEditing
    },
    RESET_LINE_ANNOTATIONS(state) {
      state.eventLineAnnotations = []
      state.activeLineAnnotation = null
      state.isTooltipShowing = false
      state.isLineAnnotationEditing = false
    },

    SALES_CHART_DATA_IS_LOADING(state, bool) {
      state.salesChartDataIsLoading = bool
    },
    CLEAR_SALES_DATA(state) {
      state.compareSalesData = defaultSalesChartContexts()
    },
    CLEAR_COMPARE_TIMESCALE_DATA(state) {
      state.initialTimescaleData = defaultSalesTimescaleContexts()
      state.compareTimescaleData = defaultSalesTimescaleContexts()
    },
    ADD_TICKET_SALES_SERIES(state, payload: EventTicketSalesChartData) {
      state.compareSalesData = payload
    },
    UPDATE_TIMESCALE_DATA_TIMEZONE(state, payload) {
      state.initialTimescaleData = payload
    },
    ADD_OVERLAY_TIMESCALE_DATA(state, payload) {
      state.compareTimescaleData = payload
    },
    UPDATE_CURRENT_EVENT_CHART_DATA_TIMEZONE(state, payload: EventTicketSalesChartData) {
      state.initialSalesData = payload
    },

    SET_INITIAL_TIMESCALE_DATA(state, payload: EventTimescaleDataContexts) {
      state.initialTimescaleData = Object.freeze(payload)
    },
    SET_INITIAL_TICKET_SALES_CHART_DATA(state, payload: EventTicketSalesChartData) {
      state.initialSalesData = Object.freeze(payload)
    },
    SET_RAW_SALES_CHART_DATA(state, payload: EventTicketSalesChartRawData) {
      state.rawSalesChartData = payload
    },
    SET_RAW_SALES_OVERLAY_DATA(state, payload: EventTicketSalesChartRawData[]) {
      state.rawSalesOverlayData = payload
    },
    RESET_EVENTS(state) {
      state.events = []
      state.isFetchingEvents = false
      state.hasNoMoreEvents = false
      state.totalEventsCount = 0
    },
    SET_EVENTS(state, events: AREvent[]) {
      state.events = clone(events)
    },
    REMOVE_EVENT(state, eventOid: number) {
      // @ts-ignore
      state.events = clone(state.events).filter(item => item.oid !== eventOid)
      state.totalEventsCount--
    },
    CONCAT_EVENTS(state, events: AREvent[]) {
      // Ensure state.events is an array before concatenating
      if (!Array.isArray(state.events)) {
        state.events = []
      }
      state.events = clone(state.events.concat(events))
    },
    SET_TOTAL_EVENTS_COUNT(state, count: number) {
      state.totalEventsCount = count
    },
    SET_IS_FETCHING_EVENTS(state, isFetching: boolean) {
      state.isFetchingEvents = isFetching
    },
    RESET_EVENT_TICKET_SALES(state) {
      state.eventTicketSales = null
    },
    RESET_EVENT_TICKET_STATS(state) {
      state.eventTicketStats = null
    },
    SET_HAS_NO_MORE_EVENTS(state, hasNoMore: boolean) {
      state.hasNoMoreEvents = hasNoMore
    },
    // Current Event
    RESET_CURRENT_EVENT(state) {
      state.currentEvent = null
    },
    SET_CURRENT_EVENT(state, event: AREvent) {
      state.currentEvent = clone(event)
    },
    SET_EVENT_SALES_CHART_TIMEZONE(state, timezone: string) {
      state.salesChartTimezone = timezone
    },
    SET_IS_FETCHING_EVENT(state, isFetching: boolean) {
      state.isFetchingEvent = isFetching
    },
    // Scratch Event
    RESET_SCRATCH_EVENT(state) {
      state.scratchEvent = initScratchEvent()
      state.scratchAdvancedSettings = initScratchAdvancedSettings()
      state.messageListInEvent = null
    },
    RESET_SCRATCH_ADVANCED_SETTINGS(state) {
      state.scratchAdvancedSettings = initScratchAdvancedSettings()
      state.messageListInEvent = null
    },
    SET_SCRATCH_EVENT(state, event: AREvent) {
      state.scratchEvent = clone(event)
      state.messageListInEvent = null
    },
    PATCH_SCRATCH_EVENT(state, changes) {
      state.scratchEvent = mergeObjects(state.scratchEvent, changes)
    },
    INJECT_CONNECT_ZOOM_PROVIDER_INFO_TO_SCRATCH_EVENT(state, { integrationOid, accountId }: ConnectZoomProviderInfo) {
      state.scratchEvent = mergeObjects(state.scratchEvent, {
        provider: 'zoom',
        meta: {
          integrationOid,
          accountId,
        },
      })
    },
    RESET_CONNECT_ZOOM_PROVIDER_INFO(state) {
      state.connectZoomProviderInfo = null
    },
    SET_CONNECT_ZOOM_PROVIDER_INFO(state, { integrationOid, accountId }) {
      state.connectZoomProviderInfo = {
        integrationOid,
        accountId,
      }
    },
    SET_IS_UPDATING_EVENT(state, isUpdating: boolean) {
      state.isUpdatingEvent = isUpdating
    },
    SET_IS_CREATING_EVENT(state, isCreating: boolean) {
      state.isCreatingEvent = isCreating
    },
    // Events Merge
    SET_IS_MERGING_EVENTS(state, isMerging: boolean) {
      state.isMergingEvents = isMerging
    },
    // Event Ticket Stats & Sales
    SET_IS_FETCHING_EVENT_TICKET_STATS(state, isFetching: boolean) {
      state.isFetchingEventTicketStats = isFetching
    },
    SET_EVENT_TICKET_STATS(state, eventTicketStats: EventTicketStats) {
      state.eventTicketStats = clone(eventTicketStats)
    },
    SET_IS_FETCHING_EVENT_TICKET_SALES(state, isFetching: boolean) {
      state.isFetchingEventTicketSales = isFetching
    },

    SET_EVENT_TICKET_SALES(state, eventTicketSales: EventTicketSales) {
      state.eventTicketSales = clone(eventTicketSales)
    },
    SET_IS_FETCHING_ZOOM_ATTENDANCE(state, isFetching: boolean) {
      state.isFetchingZoomAttendance = isFetching
    },
    SET_MESSAGE_LIST_IN_EVENT(state, messageList: MessageList) {
      state.messageListInEvent = clone(messageList)
    },
    SET_MESSAGE_LIST_IN_ADVANCED_SETTINGS(state, messageList: any) {
      state.scratchAdvancedSettings = patchMessageListInAdvancedSettings(
        state.scratchAdvancedSettings,
        messageList,
      )
    },
    SET_TAGS_IN_ADVANCED_SETTINGS(state, tags: []) {
      state.scratchAdvancedSettings = patchTagsInAdvancedSettings(
        state.scratchAdvancedSettings,
        tags,
      )
    },
    PATCH_SCRATCH_ADVANCED_SETTINGS_TO_EVENT(state) {
      state.scratchEvent = patchScratchAdvancedSettingsToEvent(
        state.scratchEvent,
        state.scratchAdvancedSettings,
      )
    },

    // Event list
    SET_EVENTS_LIST_SEARCH(state, query: string) {
      state.eventsListSearch = query
    },
    SET_EVENTS_LIST_TOTAL_COUNT(state, count: number) {
      state.eventsListTotalCount = count
    },
    CONCAT_EVENTS_LIST(state, items: EventListType[]) {
      state.eventsList = clone(state.eventsList.concat(items))
    },
    SET_IS_FETCHING_EVENTS_LIST(state, isFetching: boolean) {
      state.isFetchingEventsList = isFetching
    },
    SET_IS_LAST_EVENT_LIST_LOADED(state, isLastEventLoaded: boolean) {
      state.isLastEventListLoaded = isLastEventLoaded
    },
    SET_EVENTS_LIST_LOADED(state, eventsLoaded: number) {
      state.eventsListLoaded = eventsLoaded
    },
    RESET_EVENTS_LIST(state) {
      state.eventsList = []
      state.isLastEventListLoaded = false
      state.eventsListTotalCount = 0
      state.eventsListLoaded = 0
    },
    SELECT_EVENT_LIST(state, { oid, value }: { oid: number, value: boolean }) {
      if (value) {
        state.selectedEventsList.push(state.eventsList.find(e => e.oid === oid))
      }
      else {
        state.selectedEventsList = state.selectedEventsList.filter(e => e.oid !== oid)
      }
    },
    SET_SELECTED_EVENTS_LIST(state, events: EventListType[]) {
      state.selectedEventsList = events
    },
    CLEAR_SELECTED_EVENTS_LIST(state) {
      state.selectedEventsList = []
    },
    RESET_ALL_EVENTS_LIST(state) {
      state.isFetchingEventsList = false
      state.eventsListSearch = ''
      state.eventsListTotalCount = 0
      state.eventsListLoaded = 0
      state.isLastEventListLoaded = false
      state.selectedEventsList = []
      state.eventsList = []
      state.unsavedEventsList = []
    },
    SET_UNSAVED_EVENTS_LIST(state, events: EventListType[]) {
      state.unsavedEventsList = events
    },
    SELECT_UNSAVED_EVENT_LIST(state, { oid, value }: { oid: number, value: boolean }) {
      if (value) {
        state.unsavedEventsList.push(state.eventsList.find(e => e.oid === oid))
      }
      else {
        state.unsavedEventsList = state.unsavedEventsList.filter(e => e.oid !== oid)
      }
    },

    // Events Page Filters
    SET_EVENTS_FILTER_DATE_RANGE(state, value: EventsFilterDateRangeType) {
      state.eventsFilterDateRange = value
    },

    SET_EVENTS_FILTER_ORDER_BY_ITEMS(state, value: string) {
      state.eventsFilterOrderByItems = value
    },

    SET_EVENTS_FILTER_GROUP_BUTTON(state, value: string) {
      state.eventsFilterGroupButton = value
    },

    SET_EVENTS_FILTER_PROVIDER(state, value: string) {
      state.eventsFilterProvider = value
    },

    SET_EVENTS_TYPE_FILTER(state, value: 'all' | 'events' | 'groups') {
      state.eventsTypeFilter = value
    },

    // Audience stats
    SET_EVENT_AUDIENCE_STATS(state, audienceStats: EventAudienceStatistics[]) {
      state.eventAudienceStats = clone(audienceStats)
    },
    RESET_EVENT_AUDIENCE_STATS(state) {
      state.eventAudienceStats = []
    },
    SET_IS_FETCHING_EVENT_AUDIENCE_STATS(state, isFetching: boolean) {
      state.isFetchingEventAudienceStats = isFetching
    },

    // Power Ups
    SET_EXISTING_POWER_UPS(state, powerUps) {
      state.existingPowerUps = clone(powerUps)
    },

    SET_IS_CREATING_POWER_UP(state, isCreating: boolean) {
      state.isCreatingPowerUp = isCreating
    },

    SET_IS_FETCHING_POWER_UPS(state, isFetching: boolean) {
      state.isFetchingPowerUps = isFetching
    },

    CLEAR_POWER_UPS(state) {
      state.existingPowerUps = []
      state.isFetchingPowerUps = false
      state.isCreatingPowerUp = false
    },

    // Insights
    SET_ENGAGEMENT_INSIGHTS(state, insights) {
      state.engagementInsights = clone(insights)
    },

    SET_IS_FETCHING_ENGAGEMENT_INSIGHTS(state, isFetching: boolean) {
      state.isFetchingEngagementInsights = isFetching
    },

    CLEAR_ENGAGEMENT_INSIGHTS(state) {
      state.engagementInsights = null
      state.isFetchingEngagementInsights = false
    },

    // Event Groups mutations
    SET_EVENT_GROUPS(state, { eventGroups, totalCount }) {
      console.log('SET_EVENT_GROUPS', eventGroups)
      state.eventGroups = eventGroups
      state.totalEventGroupsCount = totalCount
    },

    CONCAT_EVENT_GROUPS(state, eventGroups: any[]) {
      // Append new groups, avoiding duplicates
      const existingOids = new Set(state.eventGroups.map((g: any) => g.oid))
      const newGroups = eventGroups.filter((g: any) => !existingOids.has(g.oid))
      state.eventGroups = [...state.eventGroups, ...newGroups]
    },

    RESET_EVENT_GROUPS(state) {
      state.eventGroups = []
      state.totalEventGroupsCount = 0
      state.hasNoMoreEventGroups = false
    },

    SET_HAS_NO_MORE_EVENT_GROUPS(state, hasNoMore: boolean) {
      state.hasNoMoreEventGroups = hasNoMore
    },

    ADD_EVENT_GROUP(state, eventGroup) {
      state.eventGroups.unshift(eventGroup)
      state.totalEventGroupsCount += 1
    },

    UPDATE_EVENT_GROUP(state, updatedGroup) {
      const index = state.eventGroups.findIndex(group => group.oid === updatedGroup.oid)
      if (index !== -1) {
        state.eventGroups.splice(index, 1, updatedGroup)
      }
      // Also update currentEventGroup if it's the same group being updated
      if (state.currentEventGroup && state.currentEventGroup.oid === updatedGroup.oid) {
        state.currentEventGroup = updatedGroup
      }
    },

    DELETE_EVENT_GROUP(state, eventGroupOid) {
      state.eventGroups = state.eventGroups.filter(group => group.oid !== eventGroupOid)
      state.totalEventGroupsCount -= 1
    },

    SET_CURRENT_EVENT_GROUP(state, eventGroup) {
      state.currentEventGroup = eventGroup
    },

    CLEAR_CURRENT_EVENT_GROUP(state) {
      state.currentEventGroup = null
    },

    SET_IS_FETCHING_EVENT_GROUPS(state, isFetching) {
      state.isFetchingEventGroups = isFetching
    },

    SET_IS_CREATING_EVENT_GROUP(state, isCreating) {
      state.isCreatingEventGroup = isCreating
    },

    SET_IS_UPDATING_EVENT_GROUP(state, isUpdating) {
      state.isUpdatingEventGroup = isUpdating
    },

    SET_IS_ADDING_EVENTS_TO_GROUP(state, isAdding) {
      state.isAddingEventsToGroup = isAdding
    },

    SET_IS_REMOVING_EVENTS_FROM_GROUP(state, isRemoving) {
      state.isRemovingEventsFromGroup = isRemoving
    },

    // Event Orders mutations
    SET_EVENT_ORDERS(state, orders) {
      state.eventOrders = orders
    },

    SET_EVENT_ORDERS_TOTAL_COUNT(state, count) {
      // For event groups, use backend count; for single events, fallback to array length
      state.totalEventOrdersCount = count > 0 ? count : state.eventOrders.length
    },

    APPEND_EVENT_ORDERS(state, orders) {
      state.eventOrders.push(...orders)
    },

    SET_IS_FETCHING_EVENT_ORDERS(state, isFetching) {
      state.isFetchingEventOrders = isFetching
    },

    RESET_EVENT_ORDERS(state) {
      state.eventOrders = []
      state.totalEventOrdersCount = 0
      state.isFetchingEventOrders = false
    },

    // Upcoming Items mutations
    SET_UPCOMING_ITEMS(state, { upcomingItems, totalCount, hasNoMore = false }) {
      state.upcomingItems = upcomingItems
      state.totalUpcomingItemsCount = totalCount
      state.hasNoMoreUpcomingItems = hasNoMore
    },

    APPEND_UPCOMING_ITEMS(state, { upcomingItems, totalCount, hasNoMore = false }) {
      state.upcomingItems.push(...upcomingItems)
      state.totalUpcomingItemsCount = totalCount
      state.hasNoMoreUpcomingItems = hasNoMore
    },

    RESET_UPCOMING_ITEMS(state) {
      state.upcomingItems = []
      state.totalUpcomingItemsCount = 0
      state.hasNoMoreUpcomingItems = false
      state.isFetchingUpcomingItems = false
    },

    SET_IS_FETCHING_UPCOMING_ITEMS(state, isFetching) {
      state.isFetchingUpcomingItems = isFetching
    },

    REMOVE_UPCOMING_ITEM(state, { type, oid }) {
      state.upcomingItems = state.upcomingItems.filter(item =>
        !((item.type || 'event') === type && item.oid === oid),
      )
      state.totalUpcomingItemsCount = Math.max(0, state.totalUpcomingItemsCount - 1)
    },

    // Past Items mutations
    SET_PAST_ITEMS(state, { pastItems, totalCount, hasNoMore = false }) {
      state.pastItems = pastItems
      state.totalPastItemsCount = totalCount
      state.hasNoMorePastItems = hasNoMore
    },

    APPEND_PAST_ITEMS(state, { pastItems, totalCount, hasNoMore = false }) {
      state.pastItems.push(...pastItems)
      state.totalPastItemsCount = totalCount
      state.hasNoMorePastItems = hasNoMore
    },

    RESET_PAST_ITEMS(state) {
      state.pastItems = []
      state.totalPastItemsCount = 0
      state.hasNoMorePastItems = false
      state.isFetchingPastItems = false
    },

    SET_IS_FETCHING_PAST_ITEMS(state, isFetching) {
      state.isFetchingPastItems = isFetching
    },

    REMOVE_PAST_ITEM(state, { type, oid }) {
      state.pastItems = state.pastItems.filter(item =>
        !((item.type || 'event') === type && item.oid === oid),
      )
      state.totalPastItemsCount = Math.max(0, state.totalPastItemsCount - 1)
    },

    // Archived Items mutations
    SET_ARCHIVED_ITEMS(state, { archivedItems, totalCount, hasNoMore = false }) {
      state.archivedItems = archivedItems
      state.totalArchivedItemsCount = totalCount
      state.hasNoMoreArchivedItems = hasNoMore
    },

    APPEND_ARCHIVED_ITEMS(state, { archivedItems, totalCount, hasNoMore = false }) {
      state.archivedItems.push(...archivedItems)
      state.totalArchivedItemsCount = totalCount
      state.hasNoMoreArchivedItems = hasNoMore
    },

    RESET_ARCHIVED_ITEMS(state) {
      state.archivedItems = []
      state.totalArchivedItemsCount = 0
      state.hasNoMoreArchivedItems = false
      state.isFetchingArchivedItems = false
    },

    SET_IS_FETCHING_ARCHIVED_ITEMS(state, isFetching) {
      state.isFetchingArchivedItems = isFetching
    },

    REMOVE_ARCHIVED_ITEM(state, { type, oid }) {
      state.archivedItems = state.archivedItems.filter(item =>
        !((item.type || 'event') === type && item.oid === oid),
      )
      state.totalArchivedItemsCount = Math.max(0, state.totalArchivedItemsCount - 1)
    },

    REMOVE_ITEMS_BATCH(state, { type, oids }) {
      if (type === 'event') {
        const oidSet = new Set(oids)
        const upcomingRemoved = state.upcomingItems.filter(item => (item.type || 'event') === 'event' && oidSet.has(item.oid)).length
        const pastRemoved = state.pastItems.filter(item => (item.type || 'event') === 'event' && oidSet.has(item.oid)).length
        state.upcomingItems = state.upcomingItems.filter(item => !((item.type || 'event') === 'event' && oidSet.has(item.oid)))
        state.pastItems = state.pastItems.filter(item => !((item.type || 'event') === 'event' && oidSet.has(item.oid)))
        state.totalUpcomingItemsCount = Math.max(0, state.totalUpcomingItemsCount - upcomingRemoved)
        state.totalPastItemsCount = Math.max(0, state.totalPastItemsCount - pastRemoved)
      }
      else if (type === 'event-group') {
        const oidSet = new Set(oids)
        const upcomingRemoved = state.upcomingItems.filter(item => (item.type || 'event') === 'event-group' && oidSet.has(item.oid)).length
        const pastRemoved = state.pastItems.filter(item => (item.type || 'event') === 'event-group' && oidSet.has(item.oid)).length
        state.upcomingItems = state.upcomingItems.filter(item => !((item.type || 'event') === 'event-group' && oidSet.has(item.oid)))
        state.pastItems = state.pastItems.filter(item => !((item.type || 'event') === 'event-group' && oidSet.has(item.oid)))
        state.totalUpcomingItemsCount = Math.max(0, state.totalUpcomingItemsCount - upcomingRemoved)
        state.totalPastItemsCount = Math.max(0, state.totalPastItemsCount - pastRemoved)
      }
    },

    REMOVE_ARCHIVED_ITEMS_BATCH(state, { type, oids }) {
      if (type === 'event') {
        const oidSet = new Set(oids)
        const removedCount = state.archivedItems.filter(item => (item.type || 'event') === 'event' && oidSet.has(item.oid)).length
        state.archivedItems = state.archivedItems.filter(item => !((item.type || 'event') === 'event' && oidSet.has(item.oid)))
        state.totalArchivedItemsCount = Math.max(0, state.totalArchivedItemsCount - removedCount)
      }
      else if (type === 'event-group') {
        const oidSet = new Set(oids)
        const removedCount = state.archivedItems.filter(item => (item.type || 'event') === 'event-group' && oidSet.has(item.oid)).length
        state.archivedItems = state.archivedItems.filter(item => !((item.type || 'event') === 'event-group' && oidSet.has(item.oid)))
        state.totalArchivedItemsCount = Math.max(0, state.totalArchivedItemsCount - removedCount)
      }
    },
  },
  getters: {
    getTimescaleData(state): EventTicketSalesChartData {
      const data = deepmerge(state.initialTimescaleData, state.compareTimescaleData, {
        arrayMerge: (destination, source) => {
          return [...destination, ...source]
        },
      })
      return data
    },
    currentEventTimezone(state): string {
      return state.currentEvent?.timeZone!
    },
    rawSalesChartData(state): EventTicketSalesChartRawData {
      return state.rawSalesChartData!
    },
    getSalesChartTimezone(state): string {
      return state.salesChartTimezone!
    },
    getEventAnnotations(state): EventAnnotation[] {
      return state.eventAnnotations
    },
    defaultEventStartTime(): string {
      return generateDefaultEventStartTime()
    },
    salesChartDataIsLoading(state): boolean {
      return state.salesChartDataIsLoading
    },
    salesChartData(state, getters): EventTicketSalesChartData {
      const chartData = deepmerge(state.initialSalesData, state.compareSalesData, {
        arrayMerge: (destination, source) => {
          return [...destination, ...source]
        },
      })

      return chartData
    },
    currentEventHasProvider(state): boolean {
      if (!state.currentEvent) {
        return false
      }
      return !!state.currentEvent.provider
    },
    scratchEventIsZoomEvent(state): boolean {
      if (!state.scratchEvent) {
        return false
      }
      return state.scratchEvent.provider === 'zoom'
    },
    scratchEventIsPhysicalEvent(state): boolean {
      if (!state.scratchEvent) {
        return false
      }
      return !!state.scratchEvent.location
    },
    zoomUrlFromCurrentEvent(state) {
      if (!state.currentEvent) {
        return null
      }
      return getZoomUrlFromCurrentEvent(state.currentEvent)
    },
    getEventImageUrl(): (event: AREvent) => string | null {
      return (event: AREvent) => {
        return getEventImageUrl(event)
      }
    },
    scratchEventImageUrl(state): string | null {
      if (!state.scratchEvent) {
        return null
      }
      return getEventImageUrl(state.scratchEvent)
    },
    getEventTotalTicketSales() {
      return (event: AREvent): number => {
        if (!event) {
          return 0
        }
        return getEventTotalTicketSales(event)
      }
    },
    getEventTotalTicketSold() {
      return (event: AREvent): number => {
        if (!event) {
          return 0
        }
        return getEventTotalTicketSold(event)
      }
    },
    getEventAttendees() {
      return (event: AREvent): number => {
        if (!event) {
          return 0
        }
        return getEventAttendees(event)
      }
    },
    currentEventEventbriteUrl(state): string | null {
      if (!state.currentEvent) {
        return null
      }
      return getEventbriteUrl(state.currentEvent)
    },
    // Check if the current event has "NO" RSVP Event
    currentEventHasNoRsvpEvent(state): boolean {
      if (!state.currentEvent) { return false };
      const hasRsvpEvent = !!state.currentEvent.campaigns.find(campaign => campaign.type === 'rsvp')
      return !hasRsvpEvent
    },
    eventHasIntegration() {
      return (event: AREvent, provider: string): boolean => {
        if (!event) { return false }
        return checkHasIntegration(event, provider)
      }
    },
    getFilterMap(_, __, rootState: any): any {
      const eventsDateFilterMap = rootState.segmentModule.segmentCriteriaMap?.loyaltyStartDate?.data.predicates.slice(0, 4) || []
      return {
        constraints: [],
        data: {
          predicates: eventsDateFilterMap,
        },
        type: 'date',
      }
    },
    getSelectedEventListIdsObject: (state) => {
      return state.selectedEventsList.reduce((accumulator: any, currentValue: EventListType) => {
        accumulator[currentValue.oid] = true

        return accumulator
      }, {})
    },

    // Event Groups getters
    eventGroups: state => state.eventGroups,
    eventGroupsCount: state => state.totalEventGroupsCount,
    currentEventGroup: state => state.currentEventGroup,
    isFetchingEventGroups: state => state.isFetchingEventGroups,
    isCreatingEventGroup: state => state.isCreatingEventGroup,
    isUpdatingEventGroup: state => state.isUpdatingEventGroup,
    isAddingEventsToGroup: state => state.isAddingEventsToGroup,
    isRemovingEventsFromGroup: state => state.isRemovingEventsFromGroup,
    getEventGroupByOid: state => (oid: number) => {
      return state.eventGroups.find(group => group.oid === oid)
    },

    // Upcoming Items getters
    upcomingItems: state => state.upcomingItems,
    upcomingItemsCount: state => state.totalUpcomingItemsCount,
    hasNoMoreUpcomingItems: state => state.hasNoMoreUpcomingItems,
    isFetchingUpcomingItems: state => state.isFetchingUpcomingItems,
    getUpcomingItemByOid: state => (type: string, oid: number) => {
      return state.upcomingItems.find(item => item.type === type && item.oid === oid)
    },
    upcomingEvents: (state) => {
      return state.upcomingItems.filter(item => item.type === 'event')
    },
    upcomingEventGroups: (state) => {
      return state.upcomingItems.filter(item => item.type === 'event-group')
    },

    // Past Items getters
    pastItems: state => state.pastItems,
    pastItemsCount: state => state.totalPastItemsCount,
    hasNoMorePastItems: state => state.hasNoMorePastItems,
    isFetchingPastItems: state => state.isFetchingPastItems,
    getPastItemByOid: state => (type: string, oid: number) => {
      return state.pastItems.find(item => item.type === type && item.oid === oid)
    },
    pastEvents: (state) => {
      return state.pastItems.filter(item => item.type === 'event')
    },
    pastEventGroups: (state) => {
      return state.pastItems.filter(item => item.type === 'event-group')
    },
  },
}

export default eventModule
